import React, { useCallback } from "react";
import { connect } from "react-redux";

import Form from "antd/es/form";
import { FormInstance } from "antd/es/form/hooks/useForm";
import Radio from "antd/es/radio";
import Space from "antd/es/space";
import { FieldData } from "rc-field-form/lib/interface";
import { defineMessages, useIntl } from "react-intl";

import ResourcesVisibility from "@mapmycustomers/shared/enum/ResourcesVisibility";
import Organization from "@mapmycustomers/shared/types/Organization";
import { isDefined } from "@mapmycustomers/shared/util/assert";

import RadioGroup from "@app/component/input/RadioGroup";
import OrganizationSetting from "@app/enum/OrganizationSetting";
import ResourcesSettings from "@app/scene/settings/type/ResourcesSettings";
import showSaveSettingSuccessfulMessage from "@app/scene/settings/util/showSaveSettingSuccessfulMessage";
import { getOrganizationId, getOrganizationSettings } from "@app/store/iam";
import { updateOrganization } from "@app/store/iam/actions";
import { RootState } from "@app/store/rootReducer";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useAnalytics from "@app/util/contexts/useAnalytics";
import useSetting from "@app/util/hook/useSetting";

import styles from "./Resources.module.scss";

const messages = defineMessages({
  all: {
    id: "settings.organizationDetails.resources.sharing.all",
    defaultMessage: "Within team and at organization level",
    description: "Settings organization details - Resources - Sharing - Entire Org",
  },
  managers: {
    id: "settings.organizationDetails.resources.managers",
    defaultMessage: "Team Managers:",
    description: "Settings organization details - Resources - Sharing - Managers",
  },
  members: {
    id: "settings.organizationDetails.resources.members",
    defaultMessage: "Team Members:",
    description: "Settings organization details - Resources - Sharing - Members",
  },
  none: {
    id: "settings.organizationDetails.resources.sharing.none",
    defaultMessage: "No sharing allowed",
    description: "Settings organization details - Resources - Sharing - No sharing",
  },
  owners: {
    id: "settings.organizationDetails.resources.sharing.owners",
    defaultMessage: "Team owners always have sharing ability.",
    description: "Settings organization details - Resources - Sharing - Owners description",
  },
  team: {
    id: "settings.organizationDetails.resources.sharing.team",
    defaultMessage: "Within team only",
    description: "Settings organization details - Resources - Sharing - Team only",
  },
  title: {
    id: "settings.organizationDetails.resources.title",
    defaultMessage: "Allow resources (files and links) to be shared by:",
    description: "Settings organization details - Resources - Title",
  },
});

interface Props {
  form: FormInstance<ResourcesSettings>;
  onHasChanges: () => void;
  onSaved: () => void;
  onUpdateOrganization: typeof updateOrganization.request;
  organizationId?: number;
  organizationSettings: Organization["settings"];
}

const Resources: React.FC<Props> = ({
  form,
  onHasChanges,
  onSaved,
  onUpdateOrganization,
  organizationId,
  organizationSettings,
}) => {
  const analytics = useExtendedAnalytics("Resources tab", useAnalytics());

  const intl = useIntl();

  const sharingSetting = useSetting(
    organizationSettings,
    OrganizationSetting.RESOURCE_AND_FOLDER_SHARING
  );

  const handleSubmit = useCallback(() => {
    const { manager, member } = form.getFieldsValue();
    if (organizationId) {
      onUpdateOrganization({
        callback: () => {
          showSaveSettingSuccessfulMessage(intl);
          onSaved();
        },
        organization: { id: organizationId },
        settings: [
          sharingSetting && member && manager
            ? { ...sharingSetting, value: { manager, member } }
            : undefined,
        ].filter(isDefined),
      });
    }
  }, [form, organizationId, onUpdateOrganization, sharingSetting, intl, onSaved]);

  const handleFieldsChange = useCallback(
    (changedFields: FieldData[]) => {
      changedFields.forEach((fieldData) => {
        switch ((fieldData.name as string[])[0]) {
          case "manager":
            analytics.using(["Change Manager Sharing Permission"], {
              value: fieldData.value,
            });
            break;
          case "member":
            analytics.using(["Change Member Sharing Permission"], {
              value: fieldData.value,
            });
            break;
        }
      });
      onHasChanges();
    },
    [analytics, onHasChanges]
  );

  return (
    <Form
      className={styles.container}
      form={form}
      initialValues={{
        manager: sharingSetting.value.manager,
        member: sharingSetting.value.member,
      }}
      onFieldsChange={handleFieldsChange}
      onFinish={handleSubmit}
    >
      <div>{intl.formatMessage(messages.title)}</div>

      <div>
        <div>{intl.formatMessage(messages.managers)}</div>
        <Form.Item name="manager">
          <RadioGroup size="small">
            <Space className={styles.space} direction="vertical">
              <Radio value={ResourcesVisibility.TEAM}>{intl.formatMessage(messages.team)}</Radio>
              <Radio value={ResourcesVisibility.ALL}>{intl.formatMessage(messages.all)}</Radio>
              <Radio value={ResourcesVisibility.NONE}>{intl.formatMessage(messages.none)}</Radio>
            </Space>
          </RadioGroup>
        </Form.Item>
      </div>
      <div>
        <div>{intl.formatMessage(messages.members)}</div>
        <Form.Item name="member">
          <RadioGroup size="small">
            <Space className={styles.space} direction="vertical">
              <Radio value={ResourcesVisibility.TEAM}>{intl.formatMessage(messages.team)}</Radio>
              <Radio value={ResourcesVisibility.NONE}>{intl.formatMessage(messages.none)}</Radio>
            </Space>
          </RadioGroup>
        </Form.Item>
      </div>
      <div>{intl.formatMessage(messages.owners)}</div>
    </Form>
  );
};

const mapStateToProps = (state: RootState) => ({
  organizationId: getOrganizationId(state),
  organizationSettings: getOrganizationSettings(state) ?? [],
});

const mapDispatchToProps = {
  onUpdateOrganization: updateOrganization.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(Resources);
